import {
  Supertype,
  supertypeClass,
  property,
  remote,
  Remoteable,
  Persistable,
  amorphicStatic,
} from '@haventech/amorphic';
import {
  IsomorphicPersistent,
  Persistent,
  IsomorphicQuery,
  Query,
  Isomorphic,
  Serializable,
  AppController,
  SubController,
  Everything,
} from '@hit/amorphic-types';
import { Asset } from './Asset';
import { EarnedIncome } from './EarnedIncome';
import { Applicant } from '../customer/Applicant';

@supertypeClass({ toClient: true, toServer: true })
export class Retirement401K extends Asset {
  @property({ getType: () => Applicant })
  applicant: Applicant;

  @property()
  earnedIncome: EarnedIncome;

  @property()
  monthly: number;

  @property()
  taxRate: number = 0.4;

  constructor() {
    super();
  }

  static new(earnedIncome, amount, monthly) {
    return new Retirement401K().init(earnedIncome, amount, monthly);
  }

  init(earnedIncome, amount, monthly) {
    super.init(earnedIncome ? earnedIncome.customer : null, '401K', amount);

    this.earnedIncome = earnedIncome;
    this.monthly = monthly;
    return this;
  }

  reset() {
    this.monthly = 0;
    this.amount = 0;
  }

  inCashFlow() {
    return true;
  }

  isActive() {
    return this.amount > 0 || this.monthly > 0;
  }

  isIncome() {
    return true;
  }

  getStartingFlow(deceased) {
    const flow: any = {
      amount: this.amount,
      startYear: 0,
      endYear: this.getEndYear(),
      retirementAge: this.customer.settings.retirementAge,
      contribution: this.monthly * 12,
      residualValue: 0,
      withdrawalAmount: 0,
      initialWithdrawalAmount: 0,
      valueWhenDrawnDown: 0,
      forcedDrawdown: false,
    };

    // Non-married beneficiaries must start collecting immediately
    if (deceased === this.applicant && this.customer.profile.relationship !== 'married') {
      flow.withdrawalAge = this.applicant.person.age;
      flow.forcedDrawdown = true;
    } else {
      flow.withdrawalAge = flow.retirementAge;
    }

    flow.withdrawalYear = Math.max(0, flow.withdrawalAge - this.earnedIncome.applicant.person.age);
    flow.retirementYear = Math.max(0, flow.retirementAge - this.earnedIncome.applicant.person.age);

    return flow;
  }

  adjustFlow(flow, year, deceased, yearOfDeath?) {
    const withdrawalAmount = year >= flow.withdrawalYear ? Math.min(flow.amount, flow.withdrawalAmount) : 0;

    flow.amount -= withdrawalAmount;

    // Asset drawn down
    flow.amount += flow.amount * this.customer.settings.retirementRate;

    // Asset earns
    if (year < yearOfDeath || (deceased !== this.earnedIncome.applicant && year < flow.retirementYear)) {
      flow.amount += flow.contribution;
    }

    // Contribution if still working and prior to retirement
    flow.contribution += flow.contribution * this.earnedIncome.increase;
    flow.residualValue = flow.amount;
  }

  getAmount(flow, year) {
    // Compute withdrawal amount on first year of withdrawals
    if (year === flow.withdrawalYear) {
      flow.withdrawalAmount = flow.forcedDrawdown
        ? flow.amount / this.customer.assumptions.requiredWithdrawls[flow.withdrawalAge]
        : flow.amount / (this.customer.settings.mortalityAge - flow.withdrawalAge);
      flow.initialWithdrawalAmount = flow.withdrawalAmount;
      flow.valueWhenDrawnDown = flow.amount;
    }

    let withdrawalAmount = 0;

    if (year >= flow.withdrawalYear) {
      withdrawalAmount = Math.min(flow.amount, flow.withdrawalAmount);
      flow.withdrawalAmount += flow.withdrawalAmount * this.customer.settings.inflation;
    }

    flow.residualValue = flow.amount;

    return withdrawalAmount * (1 - this.taxRate);
  }

  getDescription() {
    return this.earnedIncome.applicant.person.firstName + "'s 401K";
  }
}
